"use client";

import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";

import { type StatsigClient, useStatsigClient } from "@statsig/react-bindings";

import { useProfileSSOid } from "~/queries/profile/useProfile";

import type { StatsigUserContextValue } from "./StatsigUserProvider.types";
import { useStatsigUserWriter } from "./useStatsigUserWriter";

const StatsigUserContext = createContext<StatsigUserContextValue | undefined>(
  undefined,
);

/**
 * The single writer for the client-side Statsig user, mounted once site-wide.
 * Routing every write through here removes bugs where `updateUserAsync` callers
 * used to cause (specifically at mount). The write and concurrency behavior
 * lives in `useStatsigUserWriter`; consumers read it via `useStatsigUserContext`.
 *
 * Must sit inside `StatsigBootstrapProvider` (needs the Statsig client) and
 * below `ProfileProvider`/`QueryProvider` (reads the profile cache for
 * identity). For bots (`disabled`) it writes nothing.
 */
const StatsigUserProvider = ({
  children,
  disabled,
}: PropsWithChildren<{ disabled: boolean }>) => {
  const client = useStatsigClient().client as StatsigClient;
  const ssoId = useProfileSSOid();
  const { isSyncing, setAttributes, setLocale } = useStatsigUserWriter({
    client,
    disabled,
    ssoId,
  });

  const value = useMemo(
    () => ({ disabled, isSyncing, setAttributes, setLocale }),
    [disabled, isSyncing, setAttributes, setLocale],
  );

  return (
    <StatsigUserContext.Provider value={value}>
      {children}
    </StatsigUserContext.Provider>
  );
};

/**
 * Reads the raw `StatsigUserProvider` context.
 * includes: disabled, isSyncing, setAttributes, setLocale
 */
const useStatsigUserContext = (): StatsigUserContextValue => {
  const context = useContext(StatsigUserContext);
  if (context === undefined) {
    throw new Error(
      "useStatsigUserContext must be used within a StatsigUserProvider",
    );
  }
  return context;
};

export { StatsigUserProvider, useStatsigUserContext };
